import {useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, FormControl, InputLabel, MenuItem, Select, Typography} from '@mui/material';

import {EmpionCollapsibleSection} from '../../../../../components/EmpionCollapsibleSection/EmpionCollapsibleSection';
import EmpionDialog from '../../../../../components/EmpionDialog/EmpionDialog';
import {EmptyStateCard} from '../../../../../components/EmptyStateCard/EmptyStateCard';
import {orangeColors} from '../../../../../layouts/theme/designSystem';
import {WorkforceDashboardRecurringSkillGapByTargetProfile} from '../../types/workforceDashboardTypes';

const ALL_PROFILES_FILTER = 'all';

const groupKey = (group: WorkforceDashboardRecurringSkillGapByTargetProfile) =>
  `${group.companyDepartmentTargetProfileId}:${group.companyDepartmentId}`;

export interface Props {
  open: boolean;
  groups: WorkforceDashboardRecurringSkillGapByTargetProfile[];
  onClose: () => void;
}

export const WorkforceDashboardRecurringSkillGapsDetailsDialog = ({open, groups, onClose}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDashboardPage.sections.recurringSkillGaps.dialog'});
  const [selectedFilter, setSelectedFilter] = useState<string>(ALL_PROFILES_FILTER);
  const [expandedPanels, setExpandedPanels] = useState<Record<string, boolean>>({});

  const filteredGroups = useMemo(() => {
    if (selectedFilter === ALL_PROFILES_FILTER) {
      return groups;
    }

    return groups.filter((group) => groupKey(group) === selectedFilter);
  }, [groups, selectedFilter]);

  const handleClose = () => {
    setSelectedFilter(ALL_PROFILES_FILTER);
    setExpandedPanels({});
    onClose();
  };

  const handleFilterChange = (value: string) => {
    setSelectedFilter(value);
    setExpandedPanels({});
  };

  const togglePanel = (panelId: string) => {
    setExpandedPanels((previous) => ({
      ...previous,
      [panelId]: !previous[panelId],
    }));
  };

  return (
    <EmpionDialog
      open={open}
      onClose={handleClose}
      title={t('title') as string}
      subTitle={t('description') as string}
      showCloseButton
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, mt: 2}}>
        <FormControl fullWidth size='small'>
          <InputLabel id='recurring-skill-gaps-profile-filter-label'>{t('filterLabel')}</InputLabel>
          <Select
            labelId='recurring-skill-gaps-profile-filter-label'
            label={t('filterLabel')}
            value={selectedFilter}
            onChange={(event) => handleFilterChange(event.target.value)}
          >
            <MenuItem value={ALL_PROFILES_FILTER}>{t('filterAll')}</MenuItem>
            {groups.map((group) => (
              <MenuItem key={groupKey(group)} value={groupKey(group)}>
                {t('filterOption', {
                  targetProfileTitle: group.targetProfileTitle,
                  companyDepartmentName: group.companyDepartmentName,
                })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {filteredGroups.length === 0 ? (
          <EmptyStateCard message={t('filteredEmptyMessage') as string} />
        ) : (
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
            {filteredGroups.map((group) => {
              const panelId = groupKey(group);
              const groupMaxCount = group.skillGaps[0]?.profileCount || 1;
              const sectionTitle = t('filterOption', {
                targetProfileTitle: group.targetProfileTitle,
                companyDepartmentName: group.companyDepartmentName,
              });

              return (
                <EmpionCollapsibleSection
                  key={panelId}
                  title={sectionTitle}
                  metaText={t('sectionGapCount', {count: group.skillGaps.length}) as string}
                  collapsible
                  open={!!expandedPanels[panelId]}
                  onToggle={() => togglePanel(panelId)}
                >
                  <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75}}>
                    {group.skillGaps.map((skillGap) => (
                      <Box
                        key={`${panelId}-${skillGap.skillName}`}
                        sx={{display: 'flex', alignItems: 'center', gap: 1}}
                      >
                        <Typography variant='body2' sx={{flex: 1}}>
                          {skillGap.skillName}
                        </Typography>
                        <Box
                          sx={{
                            width: 70,
                            height: 5,
                            bgcolor: orangeColors[5],
                            borderRadius: '999px',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: `${(skillGap.profileCount / groupMaxCount) * 100}%`,
                              bgcolor: orangeColors[70],
                            }}
                          />
                        </Box>
                        <Typography variant='caption' sx={{width: 20, textAlign: 'right'}}>
                          {skillGap.profileCount}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </EmpionCollapsibleSection>
              );
            })}
          </Box>
        )}
      </Box>
    </EmpionDialog>
  );
};
